// Resilient retry primitive — the AWS Builders' Library "Full Jitter" standard.
//
// Reference: https://aws.amazon.com/builders-library/timeouts-retries-and-backoff-with-jitter/
// and https://aws.amazon.com/blogs/architecture/exponential-backoff-and-jitter/.
// The 2026 resilience consensus (AWS Well-Architected REL05-BP03; digitalapplied
// "API Error Handling and Resilience 2026") mandates exactly four things on a
// mutating client→API call, all encoded here so no caller re-implements them
// subtly wrong (the 5,150 "Kundali API unreachable after 3 attempts" failures
// came from a fixed `500 * attempt` backoff with no jitter, no idempotency key,
// and a dead-end throw):
//
//   1. Exponential backoff with FULL JITTER — sleep = random(0, min(cap, base*2^n)).
//      Full jitter spreads synchronized client retries uniformly across the
//      window so a flaky POP/ISP doesn't get a thundering-herd re-spike.
//   2. NEVER retry a 4xx — a client error (bad birth details) will fail
//      identically on retry; retrying wastes the user's time and the backend's.
//   3. One IDEMPOTENCY KEY per logical submit, reused across every attempt, so a
//      retried mutating POST cannot double-create server-side (the backend
//      dedupes by key; if it ignores the header the client still degrades safely).
//   4. A bounded attempt count, then a typed exhaustion so the UI can render a
//      user-facing retry state instead of a dead end.
//
// This module is transport-only: it knows nothing about kundali. `kundali.ts`
// composes it. Keeping it generic means any future mutating money/value path
// (matching, report checkout) adopts the same standard by calling one function.

export class RetryableHttpError extends Error {
  readonly status: number;
  /** The response's Retry-After, in ms, when it sent a valid one. */
  readonly retryAfterMs: number | null;
  constructor(status: number, message: string, retryAfterMs: number | null = null) {
    super(message);
    this.name = "RetryableHttpError";
    this.status = status;
    this.retryAfterMs = retryAfterMs;
  }
}

/**
 * Thrown when every attempt is exhausted without a terminal (non-retryable)
 * outcome. The UI uses `attempts` + `lastCause` to render a "Try again" card
 * (rule 39: one primary action) rather than a dead end. Carries the SAME
 * idempotencyKey so a manual "Try again" can reuse it — never double-creating.
 */
export class RetryExhaustedError extends Error {
  readonly attempts: number;
  readonly idempotencyKey: string;
  readonly lastCause: string;
  constructor(attempts: number, idempotencyKey: string, lastCause: string) {
    super(`Request unreachable after ${attempts} attempts (${lastCause})`);
    this.name = "RetryExhaustedError";
    this.attempts = attempts;
    this.idempotencyKey = idempotencyKey;
    this.lastCause = lastCause;
  }
}

/**
 * `retry_5xx` is a retried transient HTTP status: a 5xx, or a 429 when the
 * caller opted in with `tooManyRequests` (only idempotent server reads do).
 */
export type RetryAttemptOutcome = "ok" | "retry_4xx_skip" | "retry_network" | "retry_5xx";

/**
 * A terminal (non-retryable) error already surfaced to the caller — e.g. a 4xx
 * field-validation message. The retry loop re-throws it untouched instead of
 * treating it as a retryable network failure. Any error carrying `name` in this
 * set is terminal; `ApiError` (from `_fetch.ts`) is the canonical one.
 */
const TERMINAL_ERROR_NAMES = new Set(["ApiError"]);

function isTerminalError(err: unknown): boolean {
  return err instanceof Error && TERMINAL_ERROR_NAMES.has(err.name);
}

export interface RetryConfig {
  /** Max total attempts (inclusive). Default 3 — matches the prior loop. */
  maxAttempts?: number;
  /** Base backoff in ms before jitter. Default 500. */
  baseDelayMs?: number;
  /** Hard cap on a single jittered sleep, ms. Default 8000 (AWS cap is 20s). */
  maxDelayMs?: number;
  /** Idempotency key reused across all attempts of THIS logical submit. */
  idempotencyKey: string;
  /**
   * Per-attempt telemetry hook (rule 34 owned beacon at the call site). Fires
   * once per attempt with the 1-indexed attempt number and its outcome, so the
   * field funnel can split "network unreachable" from "bad input" from success.
   */
  onAttempt?: (attempt: number, outcome: RetryAttemptOutcome) => void;
  /** Injectable sleeper (tests pass a no-op / fake timer). */
  sleep?: (ms: number) => Promise<void>;
  /** Injectable RNG in [0,1) for deterministic jitter tests. */
  random?: () => number;
  /**
   * Opt-in: retry HTTP 429 instead of treating it as a final 4xx. ONLY for
   * idempotent reads (a GET of a deterministic answer): a retried mutating
   * request could double-apply. Absent, 429 stays terminal like every 4xx.
   */
  tooManyRequests?: TooManyRequestsPolicy;
}

/**
 * How an idempotent read rides out a 429 ("no available instance", or a rate
 * limit). Each wait is full jitter on its own base and cap, raised to the
 * response's Retry-After when it sent one. `waitBudgetMs` bounds the total time
 * spent waiting on 429s: a wait that would cross it (a long Retry-After
 * included) is never started, so the read gives up while the page render
 * still has its time budget.
 */
export interface TooManyRequestsPolicy {
  baseDelayMs: number;
  maxDelayMs: number;
  waitBudgetMs: number;
}

const DEFAULTS = { maxAttempts: 3, baseDelayMs: 500, maxDelayMs: 8000 } as const;
type RetryAttemptFn<T> = (idempotencyKey: string, attempt: number) => Promise<T>;
type RetryAttemptState<T> =
  | { kind: "success"; result: T }
  | { kind: "terminal"; error: unknown }
  | { kind: "retry"; cause: string; error: unknown };

/**
 * Full-jitter backoff for the Nth (1-indexed) attempt that is about to be
 * retried: `random(0, 1) * min(cap, base * 2^attempt)`. Exported for the
 * regression pin (rule: jitter is bounded by the cap and by base*2^attempt).
 * @internal
 */
export function fullJitterDelayMs(
  attempt: number,
  baseDelayMs: number,
  maxDelayMs: number,
  random: () => number
): number {
  const exponential = baseDelayMs * 2 ** attempt;
  const ceiling = Math.min(maxDelayMs, exponential);
  return Math.floor(random() * ceiling);
}

const defaultSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Run a mutating request with full-jitter backoff + idempotency.
 *
 * `attemptFn` receives the idempotency key (to set the `Idempotency-Key` header)
 * and the 1-indexed attempt number. It MUST:
 *   - return the parsed result on success,
 *   - throw `RetryableHttpError(status)` for an HTTP non-ok (the policy here
 *     decides 4xx = terminal, 5xx = retry),
 *   - throw any other Error for a network/transport failure (retryable).
 */
export async function withResilientRetry<T>(
  attemptFn: RetryAttemptFn<T>,
  config: RetryConfig
): Promise<T> {
  const maxAttempts = config.maxAttempts ?? DEFAULTS.maxAttempts;
  const sleep = config.sleep ?? defaultSleep;
  const key = config.idempotencyKey;
  const nextWait = backoffPlanner(config);

  let lastCause = "unknown";
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const outcome = await runRetryAttempt(attemptFn, key, attempt, config);
    if (outcome.kind === "success") return outcome.result;
    if (outcome.kind === "terminal") throw outcome.error;

    lastCause = outcome.cause;
    if (attempt === maxAttempts) break;
    const wait = nextWait(attempt, outcome.error);
    if (wait === null) {
      // A 429 wait would cross the read's budget: give up now, while the
      // caller can still render its outage path in time.
      throw new RetryExhaustedError(attempt, key, `${lastCause}; next wait over budget`);
    }
    await sleep(wait);
  }
  throw new RetryExhaustedError(maxAttempts, key, lastCause);
}

/**
 * The wait before the next attempt: full jitter on the ordinary curve, or on
 * the 429 policy's curve for an opted-in 429. Returns null when that 429 wait
 * would take the total time spent waiting on 429s past `waitBudgetMs`.
 */
function backoffPlanner(config: RetryConfig): (attempt: number, error: unknown) => number | null {
  const baseDelayMs = config.baseDelayMs ?? DEFAULTS.baseDelayMs;
  const maxDelayMs = config.maxDelayMs ?? DEFAULTS.maxDelayMs;
  const random = config.random ?? Math.random;
  const policy = config.tooManyRequests;
  let tooManyRequestsWaitedMs = 0;
  return (attempt, error) => {
    if (!policy || !isTooManyRequests(error)) {
      return fullJitterDelayMs(attempt, baseDelayMs, maxDelayMs, random);
    }
    const wait = tooManyRequestsDelayMs(attempt, error, policy, random);
    if (tooManyRequestsWaitedMs + wait > policy.waitBudgetMs) return null;
    tooManyRequestsWaitedMs += wait;
    return wait;
  };
}

function isTooManyRequests(err: unknown): err is RetryableHttpError {
  return err instanceof RetryableHttpError && err.status === 429;
}

/**
 * Full jitter on the 429 policy's own curve, never shorter than Retry-After.
 * @internal
 */
export function tooManyRequestsDelayMs(
  attempt: number,
  err: RetryableHttpError,
  policy: TooManyRequestsPolicy,
  random: () => number
): number {
  const jitter = fullJitterDelayMs(attempt, policy.baseDelayMs, policy.maxDelayMs, random);
  return Math.max(jitter, err.retryAfterMs ?? 0);
}

async function runRetryAttempt<T>(
  attemptFn: RetryAttemptFn<T>,
  key: string,
  attempt: number,
  config: RetryConfig
): Promise<RetryAttemptState<T>> {
  const onAttempt = config.onAttempt;
  try {
    const result = await attemptFn(key, attempt);
    onAttempt?.(attempt, "ok");
    return { kind: "success", result };
  } catch (err) {
    const terminalSignal = terminalRetrySignal(err, config);
    if (terminalSignal) {
      onAttempt?.(attempt, terminalSignal);
      return { kind: "terminal", error: err };
    }
    onAttempt?.(attempt, retrySignal(err));
    return { kind: "retry", cause: causeOf(err), error: err };
  }
}

function terminalRetrySignal(err: unknown, config: RetryConfig): RetryAttemptOutcome | null {
  // 4xx is terminal: the same input fails identically, so never retry. This
  // covers BOTH a RetryableHttpError in the 4xx band AND an already-surfaced
  // ApiError (the caller's attemptFn may throw the field-message error directly).
  // The one exception is a 429 on a read that opted in: it says "not now",
  // not "never", so the same request can succeed a moment later.
  if (config.tooManyRequests && isTooManyRequests(err)) return null;
  if (err instanceof RetryableHttpError && err.status >= 400 && err.status < 500) {
    return "retry_4xx_skip";
  }
  return isTerminalError(err) ? "retry_4xx_skip" : null;
}

function retrySignal(err: unknown): RetryAttemptOutcome {
  return err instanceof RetryableHttpError ? "retry_5xx" : "retry_network";
}

function causeOf(err: unknown): string {
  if (err instanceof RetryableHttpError) return `HTTP ${err.status}`;
  if (err instanceof Error) return `${err.name}: ${err.message}`;
  return String(err);
}

/**
 * Mint one idempotency key per logical browser submit. `crypto.randomUUID`
 * exists in every target browser + workerd; the timestamp fallback keeps SSR /
 * old-runtime calls safe (the key is only meaningful client-side).
 */
export function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `idk_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}
